import { Dialog as DialogPrimitive, DialogRootActions } from '@base-ui/react/dialog'
import { X } from 'lucide-react'
import * as React from 'react'

import { buttonVariants } from '@eigtech/ui/components/button'
import { cn, cva } from '@eigtech/ui/lib/utils'
import { VariantProps } from 'class-variance-authority'

const className =
  'w-full max-w-[calc(100%-2rem)] left-[50%] top-[50%] translate-x-[-50%] translate-y-[-50%]'

const dialogVariants = cva('', {
  variants: {
    size: {
      custom: className,
      sm: `${className} sm:max-w-md`,
      md: `${className} sm:max-w-lg`,
      lg: `${className} sm:max-w-xl`,
      xl: `${className} sm:max-w-2xl`,
      xxl: `${className} sm:max-w-3xl`,
      fullscreen: 'inset-5',
    },
  },
  defaultVariants: {
    size: 'md',
  },
})

type DialogContextType = {
  closeDisabled: boolean
} & VariantProps<typeof dialogVariants>

const DialogContext = React.createContext<DialogContextType>({ closeDisabled: false })

export type DialogProps = React.ComponentProps<typeof DialogPrimitive.Root> &
  Partial<DialogContextType>

function Dialog({
  closeDisabled = false,
  size,
  ...props
}: DialogProps & VariantProps<typeof dialogVariants>) {
  return (
    <DialogContext.Provider value={{ closeDisabled, size }}>
      <DialogPrimitive.Root data-slot="dialog" {...props} />
    </DialogContext.Provider>
  )
}

function DialogTrigger(props: React.ComponentProps<typeof DialogPrimitive.Trigger>) {
  return <DialogPrimitive.Trigger data-slot="dialog-trigger" {...props} />
}

function DialogPortal({ ...props }: React.ComponentProps<typeof DialogPrimitive.Portal>) {
  return <DialogPrimitive.Portal data-slot="dialog-portal" {...props} />
}

function DialogAction({ className, ...props }: React.ComponentProps<typeof DialogPrimitive.Close>) {
  return (
    <DialogPrimitive.Close
      className={cn(!props.render && buttonVariants(), className)}
      data-slot="dialog-action"
      {...props}
    />
  )
}

function DialogClose({ className, ...props }: React.ComponentProps<typeof DialogPrimitive.Close>) {
  const { closeDisabled } = React.useContext(DialogContext)

  if (closeDisabled) {
    return null
  }

  return (
    <DialogPrimitive.Close
      className={cn(!props.render && buttonVariants({ variant: 'outline' }), className)}
      data-slot="dialog-close"
      {...props}
    />
  )
}

// Base UI Dialog Backdrop
function DialogBackdrop({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Backdrop>) {
  return (
    <DialogPrimitive.Backdrop
      className={cn(
        'data-ending-style:opacity-0 data-starting-style:opacity-0 fixed inset-0 z-50 bg-black/30 transition-all duration-150 [backdrop-filter:blur(4px)]',
        className
      )}
      data-slot="dialog-backdrop"
      {...props}
    />
  )
}

function DialogPopup({ className, ...props }: React.ComponentProps<typeof DialogPrimitive.Popup>) {
  const { size } = React.useContext(DialogContext)

  return (
    <DialogPrimitive.Popup
      className={dialogVariants({
        size,
        className: cn(
          `bg-card text-card-foreground data-ending-style:scale-90 data-starting-style:scale-90 data-ending-style:opacity-0 data-starting-style:opacity-0 fixed z-50 flex flex-col gap-4 border p-6 shadow-lg shadow-black/5 transition-all duration-150 sm:rounded-lg`,
          className
        ),
      })}
      data-slot="dialog-popup"
      {...props}
    />
  )
}

export interface DialogContentProps extends React.ComponentProps<typeof DialogPrimitive.Popup> {
  showDismissButton?: boolean
  showBackdrop?: boolean
}

function DialogContent({
  className,
  children,
  showBackdrop = true,
  showDismissButton = true,
  ...props
}: DialogContentProps) {
  const { closeDisabled } = React.useContext(DialogContext)

  return (
    <DialogPortal>
      {showBackdrop && <DialogBackdrop />}

      <DialogPopup {...props} className={className}>
        {children}

        {showDismissButton && (
          <DialogPrimitive.Close
            className={cn(
              buttonVariants({ variant: 'dim', size: 'sm' }),
              'absolute end-2.5 top-2.5',
              className
            )}
            data-slot="alert-dialog-dismiss"
            disabled={closeDisabled}
          >
            <X />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>
        )}
      </DialogPopup>
    </DialogPortal>
  )
}

function DialogHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      className={cn('flex flex-col gap-2 text-center sm:text-left', className)}
      data-slot="dialog-header"
      {...props}
    />
  )
}

function DialogBody({ className, ...props }: React.ComponentProps<'div'>) {
  return <div className={cn('flex-1 py-2.5', className)} data-slot="dialog-body" {...props} />
}

function DialogFooter({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      className={cn('flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2', className)}
      data-slot="dialog-footer"
      {...props}
    />
  )
}

function DialogTitle({ className, ...props }: React.ComponentProps<typeof DialogPrimitive.Title>) {
  return (
    <DialogPrimitive.Title
      className={cn('text-lg font-semibold leading-none', className)}
      data-slot="dialog-title"
      {...props}
    />
  )
}

function DialogDescription({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Description>) {
  return (
    <DialogPrimitive.Description
      className={cn('text-muted-foreground text-sm', className)}
      data-slot="dialog-description"
      {...props}
    />
  )
}

const createHandle = DialogPrimitive.createHandle

export {
  // eslint-disable-next-line react-refresh/only-export-components
  createHandle,
  Dialog,
  DialogAction,
  DialogBackdrop,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
}

export type { DialogRootActions }
